/**
 * @fileoverview VerifyInvoiceEmail — public page for sender email verify.
 *
 * v3.0 Phase 3.5 (2026-08-29) — proactive-add authorization model. An
 * org admin/editor adds an email address in Org Settings; the sender
 * receives a link that lands them here. This page reads the token from
 * the URL, calls GET /api/email-verify/<token> to display who authorized
 * them and to what org, and then POSTs the same token when the sender
 * clicks the confirm button.
 *
 * Public — no auth required. Rendered outside the sidebar shell in
 * App.js like the sibling AcceptInvitation page.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../services/api';

/** Distinct error keys the API returns; used to pick friendly copy. */
const ERROR_COPY = {
  expired: {
    title: 'This verification link has expired',
    body: (
      'Ask the person who added you on cogslog to resend the invite '
      + 'from their Org Settings page. The new link will land in this '
      + 'inbox.'
    ),
  },
  not_found: {
    title: 'This verification link is no longer valid',
    body: (
      'The link may have already been used or the authorization may '
      + 'have been removed. If you\'re expecting to send invoices, ask '
      + 'the cogslog account holder to add your address again.'
    ),
  },
  invalid: {
    title: 'This link is not valid',
    body: (
      'The URL looks malformed. Try copy-pasting the entire link from '
      + 'your email into a new browser tab.'
    ),
  },
  rate_limited: {
    title: 'Too many attempts',
    body: (
      'We\'ve seen too many verification attempts from your network. '
      + 'Please try again in an hour.'
    ),
  },
  network: {
    title: 'Couldn\'t reach cogslog',
    body: 'Check your internet connection and try again.',
  },
};

function friendlyError(err) {
  if (!err) return ERROR_COPY.network;
  const code = err?.response?.data?.error;
  if (code && ERROR_COPY[code]) return ERROR_COPY[code];
  return {
    title: 'We couldn\'t verify this address',
    body: (
      err?.response?.data?.message
      || 'Something went wrong. Please try again in a few minutes.'
    ),
  };
}

/**
 * VerifyInvoiceEmail page.
 *
 * @returns {JSX.Element}
 */
function VerifyInvoiceEmail() {
  const [params] = useSearchParams();
  const token = params.get('token') || '';

  const [meta, setMeta] = useState(null);
  const [metaLoading, setMetaLoading] = useState(true);
  const [metaError, setMetaError] = useState(null);

  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const loadMeta = useCallback(async () => {
    if (!token) {
      setMetaError(ERROR_COPY.invalid);
      setMetaLoading(false);
      return;
    }
    setMetaLoading(true);
    setMetaError(null);
    try {
      const { data } = await api.getEmailVerify(token);
      setMeta(data);
      // If the row was already verified (e.g. sender clicked twice),
      // reflect that up front so they see the success state right away.
      if (data?.verification_status === 'verified') {
        setSubmitted(true);
      }
    } catch (err) {
      setMetaError(friendlyError(err));
    } finally {
      setMetaLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadMeta();
  }, [loadMeta]);

  async function handleConfirm() {
    if (!token || submitting) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      await api.postEmailVerify(token);
      setSubmitted(true);
    } catch (err) {
      setSubmitError(friendlyError(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="auth-page" data-testid="verify-invoice-email">
      <div className="auth-card" style={{ maxWidth: 520 }}>
        <h1 className="auth-card__title">Verify your email address</h1>

        {metaLoading && (
          <div role="status" aria-live="polite" className="ops-panel__loading">
            Loading&hellip;
          </div>
        )}

        {!metaLoading && metaError && (
          <div className="empty-state" data-testid="verify-invoice-email-error">
            <div className="empty-state__title">{metaError.title}</div>
            <p className="empty-state__description">{metaError.body}</p>
          </div>
        )}

        {!metaLoading && !metaError && meta && (
          <>
            {!submitted && (
              <>
                <p>
                  You&rsquo;re verifying invoice email{' '}
                  <strong data-testid="verify-invoice-email-address">
                    {meta.email}
                  </strong>{' '}
                  for{' '}
                  <strong data-testid="verify-invoice-email-org">
                    {meta.org_name}
                  </strong>.
                </p>
                <p className="hint">
                  Confirm you own this address to let cogslog process
                  invoices you send to invoices@cogslog.com for this
                  organization.
                </p>

                {submitError && (
                  <div className="err-msg" role="alert">
                    {submitError.title}: {submitError.body}
                  </div>
                )}

                <div style={{ marginTop: 'var(--sp5)' }}>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleConfirm}
                    disabled={submitting}
                    data-testid="verify-invoice-email-confirm-btn"
                  >
                    {submitting ? 'Verifying…' : 'Confirm ownership'}
                  </button>
                </div>
              </>
            )}

            {submitted && (
              <div
                className="empty-state"
                data-testid="verify-invoice-email-success"
              >
                <div className="empty-state__title">
                  Verified &mdash; you&rsquo;re all set
                </div>
                <p className="empty-state__description">
                  Future invoices sent from{' '}
                  <strong>{meta.email}</strong> to{' '}
                  <strong>invoices@cogslog.com</strong> will be
                  processed automatically for{' '}
                  <strong>{meta.org_name}</strong>.
                </p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default VerifyInvoiceEmail;
